/**
 * CONSAI — Signup Wizard (4 steps)
 * Design: matches signup_wizrd_flow.html from Google Stitch
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const Signup = () => {
  const { t, language, setLanguage, toggleLanguage } = useLanguage();
  const { signup, sendOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [signupMethod, setSignupMethod] = useState('email');
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const otpRefs = useRef([]);

  const otp = otpDigits.join('');

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/[^0-9]/g, '').slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      const newDigits = [...otpDigits];
      newDigits[index - 1] = '';
      setOtpDigits(newDigits);
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleLanguageSelect = () => { setLanguage(selectedLanguage); setStep(2); };
  const handleMethodSelect = (method) => { setSignupMethod(method); setStep(3); };

  const validateForm = () => {
    if (!name.trim()) { toast.error(language === 'hi' ? 'नाम आवश्यक है' : 'Name is required'); return false; }
    if (signupMethod === 'email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error(t('invalidEmail')); return false; }
    } else {
      const clean = mobile.replace(/[^0-9]/g, '').slice(-10);
      if (!/^[6-9]\d{9}$/.test(clean)) { toast.error(t('invalidMobile')); return false; }
    }
    if (password.length < 8) { toast.error(t('passwordTooShort')); return false; }
    if (password !== confirmPassword) { toast.error(t('passwordsMismatch')); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      if (signupMethod === 'mobile') {
        await sendOtp(mobile);
        toast.success(t('otpSent'));
        setStep(4);
      } else {
        const result = await signup({
          email, password, name,
          language_preference: selectedLanguage === 'hi' ? 'hindi' : 'english'
        });
        if (result.success) {
          toast.success(t('signupSuccess'));
          localStorage.removeItem('consai_cr_fired');
          localStorage.removeItem('cr_start_time');
          sessionStorage.setItem('cr_pending', 'true');
          sessionStorage.setItem('just_signed_up', 'true');
          sessionStorage.setItem('signup_session', 'true');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('somethingWentWrong'));
    } finally { setLoading(false); }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error(language === 'hi' ? 'कृपया 6 अंकों का OTP दर्ज करें' : 'Please enter 6-digit OTP'); return; }
    setLoading(true);
    try {
      const verifyResult = await verifyOtp(mobile, otp);
      if (verifyResult?.success === false) { toast.error(verifyResult.message || t('somethingWentWrong')); return; }
      const result = await signup({
        mobile_number: mobile, password, name,
        language_preference: selectedLanguage === 'hi' ? 'hindi' : 'english'
      });
      if (result.success) {
        toast.success(t('signupSuccess'));
        localStorage.removeItem('consai_cr_fired');
        localStorage.removeItem('cr_start_time');
        sessionStorage.setItem('cr_pending', 'true');
        sessionStorage.setItem('just_signed_up', 'true');
        sessionStorage.setItem('signup_session', 'true');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('somethingWentWrong'));
    } finally { setLoading(false); }
  };

  const handleGoogleSignIn = () => {
    localStorage.setItem('consai_signup_language', selectedLanguage);
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputStyle = {
    width: '100%', background: '#0e0e0e', border: '1px solid #444748',
    borderRadius: '8px', padding: '12px 16px', color: '#ffffff',
    fontFamily: 'Manrope, sans-serif', fontSize: '16px', outline: 'none', boxSizing: 'border-box'
  };

  const primaryBtnStyle = {
    width: '100%', height: '48px', background: '#ffffff', color: '#2f3131',
    borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '700',
    fontFamily: 'Manrope, sans-serif', fontSize: '16px', display: 'flex',
    alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'opacity 0.2s'
  };

  return (
    <div
      data-testid="signup-page"
      style={{ backgroundColor: '#0A0A0B', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: 'Manrope, sans-serif' }}
    >
      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

        {/* Brand Header */}
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '6px' }}>
            <img src="/consai-logo.png" alt="CONSAI" style={{ height: '40px', width: 'auto' }} />
            <h1 className="horizon-font tracking-widest" style={{ fontSize: '42px', lineHeight: 1, margin: 0 }}>
              <span style={{ color: '#ffffff' }}>CONS</span><span style={{ color: '#d7263d' }}>AI</span>
            </h1>
          </div>
          <p style={{ color: '#c4c7c8', fontFamily: 'Manrope, sans-serif', fontSize: '14px', letterSpacing: '0.05em' }}>Enterprise Intelligence Platform</p>
        </div>

        {/* Wizard Card */}
        <div style={{ background: 'rgba(19,19,19,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '32px', width: '100%', minHeight: '520px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>

          {/* Step Indicators */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '40px' }}>
            {[1, 2, 3, 4].map((s) => (
              <div key={`step-indicator-${s}`} style={{ height: '4px', width: step === s ? '24px' : '16px', borderRadius: '9999px', background: step === s ? '#ffffff' : '#353534', transition: 'all 0.3s ease' }} />
            ))}
          </div>

          {/* STEP 1: Language */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }} data-testid="signup-step-1">
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '28px', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>Preferred Language</h2>
              <p style={{ color: '#c4c7c8', fontSize: '14px', marginBottom: '32px' }}>Select your interface language to continue.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[{ val: 'en', label: 'English', sub: 'Default System Language', icon: 'language' }, { val: 'hi', label: 'हिंदी', sub: 'हिन्दी इंटरफ़ेस', icon: 'translate' }].map(({ val, label, sub, icon }) => (
                  <label key={val} onClick={() => setSelectedLanguage(val)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', borderRadius: '8px', border: `1px solid ${selectedLanguage === val ? '#ffffff' : '#444748'}`, background: selectedLanguage === val ? 'rgba(255,255,255,0.05)' : '#0e0e0e', cursor: 'pointer', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span className="material-symbols-outlined" style={{ color: '#c4c7c8', fontSize: '22px' }}>{icon}</span>
                      <div>
                        <p style={{ color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '16px', fontWeight: '500' }}>{label}</p>
                        <p style={{ color: '#c4c7c8', fontSize: '12px' }}>{sub}</p>
                      </div>
                    </div>
                    <input type="radio" name="language" value={val} checked={selectedLanguage === val} onChange={() => setSelectedLanguage(val)} data-testid={`language-${val}`} style={{ accentColor: '#ffffff' }} />
                  </label>
                ))}
              </div>
              <div style={{ marginTop: 'auto', paddingTop: '40px' }}>
                <button onClick={handleLanguageSelect} style={primaryBtnStyle} data-testid="continue-language-btn">
                  Continue <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>arrow_forward</span>
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Method */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }} data-testid="signup-step-2">
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '28px', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                {language === 'hi' ? 'CONSAI में शामिल हों' : 'Join CONSAI'}
              </h2>
              <p style={{ color: '#c4c7c8', fontSize: '14px', marginBottom: '32px' }}>
                {language === 'hi' ? 'पंजीकरण विधि चुनें' : 'Choose your preferred registration method.'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { method: 'email', icon: 'mail', label: language === 'hi' ? 'ईमेल से जारी रखें' : 'Continue with Email', testId: 'method-email' },
                  { method: 'mobile', icon: 'phone', label: language === 'hi' ? 'मोबाइल से जारी रखें' : 'Continue with Mobile', testId: 'method-mobile' }
                ].map(({ method, icon, label, testId }) => (
                  <button key={method} onClick={() => handleMethodSelect(method)} data-testid={testId}
                    style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', borderRadius: '8px', border: '1px solid #444748', background: '#0e0e0e', color: '#ffffff', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.2s', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '16px', fontWeight: '500' }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#444748'}>
                    <span className="material-symbols-outlined" style={{ color: '#c4c7c8', fontSize: '22px' }}>{icon}</span>
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ position: 'relative', margin: '24px 0' }}>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '100%', borderTop: '1px solid #444748' }} />
                </div>
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', fontSize: '12px' }}>
                  <span style={{ background: '#131313', padding: '0 12px', color: '#c4c7c8', fontFamily: 'Manrope, sans-serif', letterSpacing: '0.05em', fontWeight: '700' }}>
                    {language === 'hi' ? 'या' : 'OR'}
                  </span>
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
                  <span style={{ background: '#22c55e', color: '#ffffff', fontSize: '10px', fontWeight: '700', padding: '2px 10px', borderRadius: '9999px', fontFamily: 'Manrope, sans-serif' }}>
                    {language === 'hi' ? 'सुझाया गया' : 'Recommended'}
                  </span>
                </div>
                <button onClick={handleGoogleSignIn} data-testid="google-signup-button"
                  style={{ width: '100%', height: '48px', background: '#0e0e0e', border: '2px solid rgba(34,197,94,0.6)', borderRadius: '8px', color: '#ffffff', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '16px', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(34,197,94,0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#0e0e0e'}>
                  <svg style={{ width: '20px', height: '20px' }} viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  {t('signUpWithGoogle')}
                </button>
              </div>
              <button onClick={() => setStep(1)} data-testid="back-to-language-btn"
                style={{ marginTop: '16px', width: '100%', height: '40px', background: 'none', border: 'none', color: '#c4c7c8', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '14px' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#c4c7c8'}>
                ← {language === 'hi' ? 'वापस' : 'Back'}
              </button>
            </div>
          )}

          {/* STEP 3: Details Form */}
          {step === 3 && (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px', flex: 1 }} data-testid="signup-step-3">
              <div>
                <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '28px', fontWeight: '600', color: '#ffffff', marginBottom: '4px' }}>
                  {language === 'hi' ? 'खाता बनाएं' : 'Create Account'}
                </h2>
                <p style={{ color: '#c4c7c8', fontSize: '14px' }}>{language === 'hi' ? 'अपनी जानकारी दर्ज करें' : 'Enter your details'}</p>
              </div>

              {/* Full Name */}
              <div>
                <label style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#c4c7c8', display: 'block', marginBottom: '8px' }}>
                  {language === 'hi' ? 'पूरा नाम' : 'Full Name'}
                </label>
                <div style={{ position: 'relative' }}>
                  <span className="material-symbols-outlined" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#c4c7c8', fontSize: '20px', pointerEvents: 'none' }}>person</span>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder={language === 'hi' ? 'अपना नाम दर्ज करें' : 'Enter your full name'}
                    style={{ ...inputStyle, paddingLeft: '44px' }} data-testid="signup-name-input"
                    onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                    onBlur={(e) => e.target.style.borderColor = '#444748'} />
                </div>
              </div>

              {/* Email or Mobile */}
              <div>
                <label style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#c4c7c8', display: 'block', marginBottom: '8px' }}>
                  {signupMethod === 'email' ? (language === 'hi' ? 'ईमेल' : 'Email') : (language === 'hi' ? 'मोबाइल नंबर' : 'Mobile Number')}
                </label>
                <div style={{ position: 'relative' }}>
                  <span className="material-symbols-outlined" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#c4c7c8', fontSize: '20px', pointerEvents: 'none' }}>
                    {signupMethod === 'email' ? 'mail' : 'phone'}
                  </span>
                  <input
                    type={signupMethod === 'email' ? 'email' : 'tel'}
                    value={signupMethod === 'email' ? email : mobile}
                    onChange={(e) => signupMethod === 'email' ? setEmail(e.target.value) : setMobile(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                    placeholder={signupMethod === 'email' ? 'you@example.com' : '9876543210'}
                    style={{ ...inputStyle, paddingLeft: '44px' }}
                    data-testid={signupMethod === 'email' ? 'signup-email-input' : 'signup-mobile-input'}
                    onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                    onBlur={(e) => e.target.style.borderColor = '#444748'} />
                </div>
              </div>

              {/* Password */}
              <div>
                <label style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#c4c7c8', display: 'block', marginBottom: '8px' }}>
                  {language === 'hi' ? 'पासवर्ड' : 'Password'}
                </label>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" style={{ ...inputStyle, paddingRight: '44px' }} data-testid="signup-password-input"
                    onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                    onBlur={(e) => e.target.style.borderColor = '#444748'} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#c4c7c8' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{showPassword ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
                <p style={{ marginTop: '6px', fontSize: '12px', color: '#8e9192' }}>
                  {language === 'hi' ? 'न्यूनतम 8 अक्षर' : 'Minimum 8 characters'}
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#c4c7c8', display: 'block', marginBottom: '8px' }}>
                  {language === 'hi' ? 'पासवर्ड की पुष्टि करें' : 'Confirm Password'}
                </label>
                <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••" style={inputStyle} data-testid="signup-confirm-password-input"
                  onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                  onBlur={(e) => e.target.style.borderColor = '#444748'} />
              </div>

              <button type="submit" disabled={loading} style={{ ...primaryBtnStyle, opacity: loading ? 0.8 : 1 }} data-testid="signup-submit-button">
                {loading ? <><Loader2 size={18} className="animate-spin" /> {language === 'hi' ? 'खाता बन रहा है...' : 'Creating account...'}</> :
                  (signupMethod === 'mobile' ? (language === 'hi' ? 'OTP भेजें' : 'Send OTP') : (language === 'hi' ? 'खाता बनाएं' : 'Create Account'))}
              </button>

              <button type="button" onClick={() => setStep(2)} data-testid="back-to-method-btn"
                style={{ width: '100%', height: '40px', background: 'none', border: 'none', color: '#c4c7c8', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '14px' }}>
                ← {language === 'hi' ? 'वापस' : 'Back'}
              </button>
            </form>
          )}

          {/* STEP 4: OTP */}
          {step === 4 && (
            <form onSubmit={handleOtpVerify} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', flex: 1 }} data-testid="signup-step-4">
              <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                <div style={{ width: '64px', height: '64px', background: '#2a2a2a', border: '1px solid #444748', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <span className="material-symbols-outlined" style={{ color: '#ffffff', fontSize: '28px' }}>sms</span>
                </div>
                <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '28px', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                  {language === 'hi' ? 'OTP सत्यापित करें' : 'Verify Identity'}
                </h2>
                <p style={{ color: '#c4c7c8', fontSize: '14px' }}>
                  {language === 'hi' ? `${mobile} पर OTP भेजा गया` : `Code sent to ${mobile}`}
                </p>
              </div>

              {/* 6-Box OTP */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', width: '100%' }}>
                {otpDigits.map((digit, index) => (
                  <input
                    key={`otp-box-${index}`}
                    ref={(el) => { otpRefs.current[index] = el; }}
                    type="number"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    data-testid={`signup-otp-input-${index}`}
                    style={{ width: '48px', height: '56px', background: '#0e0e0e', border: '1px solid #444748', borderRadius: '6px', textAlign: 'center', color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '24px', fontWeight: '600', outline: 'none', MozAppearance: 'textfield' }}
                    onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                    onBlur={(e) => e.target.style.borderColor = '#444748'}
                  />
                ))}
              </div>

              <button type="submit" disabled={loading} style={{ ...primaryBtnStyle, width: '100%', opacity: loading ? 0.8 : 1 }} data-testid="verify-otp-button">
                {loading ? <><Loader2 size={18} className="animate-spin" /> {language === 'hi' ? 'सत्यापित हो रहा है...' : 'Verifying...'}</> : (language === 'hi' ? 'OTP सत्यापित करें' : 'Verify Code')}
              </button>

              <div style={{ textAlign: 'center', fontFamily: 'Manrope, sans-serif', fontSize: '14px', color: '#c4c7c8' }}>
                {language === 'hi' ? 'OTP नहीं मिला? ' : "Didn't receive code? "}
                <button type="button" onClick={() => { setOtpDigits(['', '', '', '', '', '']); setStep(3); }}
                  style={{ color: '#F72585', fontWeight: '700', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '14px' }}>
                  {language === 'hi' ? 'पुनः भेजें' : 'Resend'}
                </button>
              </div>

              <button type="button" onClick={() => setStep(3)}
                style={{ width: '100%', height: '40px', background: 'none', border: 'none', color: '#c4c7c8', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '14px' }}>
                ← {language === 'hi' ? 'वापस' : 'Back'}
              </button>
            </form>
          )}
        </div>

        {/* Footer Links */}
        {step !== 4 && (
          <p style={{ marginTop: '24px', textAlign: 'center', fontSize: '14px', color: '#c4c7c8', fontFamily: 'Manrope, sans-serif' }}>
            {t('haveAccount')}{' '}
            <Link to="/login" style={{ color: '#ffffff', fontWeight: '700', textDecoration: 'none' }}
              data-testid="login-link" className="hover:text-consai-pink">
              {t('login')}
            </Link>
          </p>
        )}
        <p style={{ marginTop: '16px', textAlign: 'center', fontSize: '11px', color: 'rgba(196,199,200,0.4)', fontFamily: 'Manrope, sans-serif', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          © 2026 BUFFETISM IT VENTURES PVT LTD
        </p>
      </div>
    </div>
  );
};

export default Signup;
